import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { phoneAPI, bidAPI } from '../services/api';
import { useAuth } from '../context/AuthContext';
import './PhoneDetail.css';

const PLACEHOLDER_IMAGE = "data:image/svg+xml,%3Csvg xmlns=%27http://www.w3.org/2000/svg%27 width=%27600%27 height=%27600%27%3E%3Crect width=%27600%27 height=%27600%27 fill=%27%23667eea%27/%3E%3Ctext x=%2750%25%27 y=%2750%25%27 font-size=%2732%27 fill=%27white%27 text-anchor=%27middle%27 dominant-baseline=%27middle%27 font-family=%27Arial%27%3ESIN IMAGEN%3C/text%3E%3C/svg%3E";

const PhoneDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [phone, setPhone] = useState(null);
  const [bids, setBids] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [zoomPosition, setZoomPosition] = useState({ x: 0, y: 0 });
  const [isZooming, setIsZooming] = useState(false);
  const [showFullImage, setShowFullImage] = useState(false);
  const [bidAmount, setBidAmount] = useState('');
  const [submittingBid, setSubmittingBid] = useState(false);

  useEffect(() => {
    loadPhone();
    loadBids();
  }, [id]);

  const loadPhone = async () => {
    try {
      const response = await phoneAPI.getById(id);
      setPhone(response.data);
      if (response.data.precio_base) {
        setBidAmount(Math.ceil(parseFloat(response.data.precio_base) + 1));
      }
    } catch (error) {
      console.error('Error loading phone:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadBids = async () => {
    try {
      const response = await bidAPI.getPhoneBids(id);
      setBids(response.data);
    } catch (error) {
      console.error('Error loading bids:', error);
    }
  };

  const handleSubmitBid = async (e) => {
    e.preventDefault();
    
    const amount = parseInt(bidAmount);
    const precioBase = parseFloat(phone.precio_base);
    
    if (amount <= precioBase) {
      alert(`❌ Tu oferta debe ser mayor al precio base de RD$ ${precioBase.toLocaleString()}`);
      return;
    }
    
    if (amount % 1 !== 0) {
      alert('❌ La oferta debe ser un número entero sin centavos');
      return;
    }
    
    setSubmittingBid(true);
    try {
      await bidAPI.create({
        phone_id: id,
        amount: amount
      });
      alert('✅ ¡Oferta registrada exitosamente!');
      setBidAmount(amount + 1);
      loadBids();
    } catch (error) {
      console.error('Error submitting bid:', error);
      alert('❌ ' + (error.response?.data?.message || 'Error al registrar la oferta'));
    } finally {
      setSubmittingBid(false);
    }
  };

  const getHighestBid = () => {
    if (bids.length === 0) return null;
    return bids.reduce((max, bid) => bid.amount > max.amount ? bid : max, bids[0]);
  };

  const getUserBid = () => {
    if (!user) return null;
    return bids.find(bid => bid.username === user.username);
  };

  const handleMouseMove = (e) => {
    const { left, top, width, height } = e.target.getBoundingClientRect();
    const x = ((e.clientX - left) / width) * 100;
    const y = ((e.clientY - top) / height) * 100;
    setZoomPosition({ x, y });
  };

  const getGradoText = (grado) => {
    switch(grado) {
      case 'A': return '🟢 Grado A - Excelente';
      case 'B': return '🟡 Grado B - Bueno';
      case 'C': return '🔴 Grado C - Regular';
      default: return '🟡 Grado B - Bueno';
    }
  };

  const getImageUrl = (imagePath) => {
    if (!imagePath) return PLACEHOLDER_IMAGE;
    return `${process.env.REACT_APP_API_URL?.replace('/api', '') || 'http://localhost:5003'}${imagePath}`;
  };

  if (loading) {
    return (
      <div className="loading-container">
        <div className="spinner"></div>
        <p>Cargando detalles...</p>
      </div>
    );
  }

  if (!phone) {
    return (
      <div className="error-container">
        <p>❌ Teléfono no encontrado</p>
        <button onClick={() => navigate('/')}>Volver al catálogo</button>
      </div>
    );
  }

  const images = phone.images && phone.images.length > 0 ? phone.images : [null];
  const highestBid = getHighestBid();
  const userBid = getUserBid();

  return (
    <div className="detail-container">
      <button className="btn-back" onClick={() => navigate('/')}>
        ← Volver al Catálogo
      </button>

      <div className="detail-card">
        <div className="detail-images">
          <div 
            className={`main-image ${isZooming ? 'zooming' : ''}`}
            onMouseEnter={() => setIsZooming(true)}
            onMouseLeave={() => setIsZooming(false)}
            onMouseMove={handleMouseMove}
            onClick={() => setShowFullImage(true)}
          >
            {phone.vendido && (
              <div className="sold-overlay">
                <div className="sold-badge-large">
                  ✅ VENDIDO
                </div>
              </div>
            )}
            <img 
              src={getImageUrl(images[currentImageIndex])} 
              alt={`${phone.marca} ${phone.modelo}`}
              style={isZooming ? {
                transformOrigin: `${zoomPosition.x}% ${zoomPosition.y}%`
              } : {}}
              onError={(e) => {
                e.target.src = PLACEHOLDER_IMAGE;
              }}
            />
            {isZooming && (
              <div className="zoom-hint">🔍 Haz click para ver en tamaño completo</div>
            )}
          </div>
          
          {images.length > 1 && (
            <div className="image-thumbnails">
              {images.map((img, index) => (
                <img
                  key={index}
                  src={getImageUrl(img)}
                  alt={`Miniatura ${index + 1}`}
                  className={currentImageIndex === index ? 'active' : ''}
                  onClick={() => setCurrentImageIndex(index)}
                  onError={(e) => {
                    e.target.src = PLACEHOLDER_IMAGE;
                  }}
                />
              ))}
            </div>
          )}
        </div>

        <div className="detail-info">
          <div className="inventory-header">
            <span className="inventory-id">📋 ID: #{phone.id}</span>
            {phone.numero_activo && <span className="activo-number">🏷️ {phone.numero_activo}</span>}
            {phone.sistema_operativo && (
              <span className="os-badge">
                {phone.sistema_operativo === 'iOS' ? '🍎 iOS' : '🤖 Android'}
              </span>
            )}
          </div>
          
          <h1>{phone.marca}</h1>
          <h2>{phone.modelo}</h2>

          <div className="info-section">
            <h3>⭐ Condición</h3>
            <div className={`condition-display grado-${phone.grado || 'B'}`}>
              <span className="grado-text-large">{getGradoText(phone.grado)}</span>
            </div>
          </div>

          {phone.precio_base && (
            <div className="info-section price-section">
              <h3>💰 Precio Base</h3>
              <p className="price-large">RD$ {parseFloat(phone.precio_base).toLocaleString()}</p>
            </div>
          )}

          {!phone.vendido && phone.precio_base && user && (
            <div className="info-section bidding-section">
              <h3>💰 Sistema de Ofertas</h3>
              
              {highestBid && (
                <div className="highest-bid-display">
                  <span className="label">Oferta Más Alta:</span>
                  <span className="highest-amount">
                    RD$ {parseFloat(highestBid.amount).toLocaleString()}
                  </span>
                  <span className="bidder">por {highestBid.username}</span>
                </div>
              )}

              {userBid && (
                <div className="user-bid-display">
                  <span className="label">Tu Oferta:</span>
                  <span className="user-amount">
                    RD$ {parseFloat(userBid.amount).toLocaleString()}
                  </span>
                  {userBid.amount === highestBid?.amount && (
                    <span className="winning-badge-small">🏆 ¡Vas ganando!</span>
                  )}
                </div>
              )}

              <form onSubmit={handleSubmitBid} className="bid-form">
                <div className="bid-input-group">
                  <label>Hacer una Oferta</label>
                  <input
                    type="number"
                    value={bidAmount}
                    onChange={(e) => setBidAmount(e.target.value)}
                    min={Math.ceil(parseFloat(phone.precio_base) + 1)}
                    step="1"
                    required
                    placeholder="Ingresa tu oferta"
                  />
                  <small>Monto mínimo: RD$ {Math.ceil(parseFloat(phone.precio_base) + 1).toLocaleString()} (sin centavos)</small>
                </div>
                <button type="submit" disabled={submittingBid} className="btn-submit-bid">
                  {submittingBid ? '⏳ Enviando...' : '💸 Hacer Oferta'}
                </button>
              </form>

              {bids.length > 0 && (
                <div className="bids-history">
                  <h4>📊 Historial de Ofertas ({bids.length})</h4>
                  <div className="bids-list">
                    {bids.slice(0, 5).map((bid, index) => (
                      <div key={bid.id} className={`bid-item ${index === 0 ? 'highest' : ''}`}>
                        <span className="bid-rank">#{index + 1}</span>
                        <span className="bid-user">{bid.username}</span>
                        <span className="bid-amount">
                          RD$ {parseFloat(bid.amount).toLocaleString()}
                        </span>
                        {index === 0 && <span className="crown">👑</span>}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {phone.vendido && (
            <div className="info-section sold-section">
              <h3>Estado</h3>
              <p className="sold-large">✅ NO DISPONIBLE</p>
            </div>
          )}

          <div className="info-section">
            <h3>📋 Información Técnica</h3>
            <div className="info-grid">
              {phone.imei && (
                <div className="info-item">
                  <span className="label">IMEI:</span>
                  <span className="value">{phone.imei}</span>
                </div>
              )}
              {phone.serial && (
                <div className="info-item">
                  <span className="label">Serial:</span>
                  <span className="value">{phone.serial}</span>
                </div>
              )}
              {phone.status && (
                <div className="info-item">
                  <span className="label">Estado:</span>
                  <span className="value">{phone.status}</span>
                </div>
              )}
              {phone.ubicacion && (
                <div className="info-item">
                  <span className="label">Ubicación:</span>
                  <span className="value">{phone.ubicacion}</span>
                </div>
              )}
              {phone.user && (
                <div className="info-item">
                  <span className="label">Usuario:</span>
                  <span className="value">{phone.user}</span>
                </div>
              )}
            </div>
          </div>

          {phone.condicion && (
            <div className="info-section">
              <h3>📝 Condición Detallada</h3>
              <p className="description">{phone.condicion}</p>
            </div>
          )}

          {phone.comentarios && (
            <div className="info-section">
              <h3>💬 Comentarios</h3>
              <p className="description">{phone.comentarios}</p>
            </div>
          )}
        </div>
      </div>

      {showFullImage && (
        <div className="image-modal" onClick={() => setShowFullImage(false)}>
          <div className="modal-content">
            <button className="btn-close-modal" onClick={() => setShowFullImage(false)}>
              ✕ Cerrar
            </button>
            <img 
              src={getImageUrl(images[currentImageIndex])} 
              alt={`${phone.marca} ${phone.modelo}`}
              onClick={(e) => e.stopPropagation()}
            />
            {images.length > 1 && (
              <div className="modal-navigation">
                <button 
                  className="btn-prev"
                  onClick={(e) => {
                    e.stopPropagation();
                    setCurrentImageIndex(prev => prev > 0 ? prev - 1 : images.length - 1);
                  }}
                >
                  ← Anterior
                </button>
                <span className="image-counter">{currentImageIndex + 1} / {images.length}</span>
                <button 
                  className="btn-next"
                  onClick={(e) => {
                    e.stopPropagation();
                    setCurrentImageIndex(prev => prev < images.length - 1 ? prev + 1 : 0);
                  }}
                >
                  Siguiente →
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default PhoneDetail;
